import { useEffect, useMemo, useState } from "react";
import { useCesiumContext } from "./CesiumContext";
import {
  Cartesian3,
  Cartographic,
  EllipsoidGeodesic,
  Entity,
  JulianDate,
  SampledPositionProperty,
} from "@cesium/engine";

import { LabeledValue } from "../base/LabeledValue";
import { isDefined, niceFormatInterval } from "../../lib/utils";
import { formatUnitValue } from "../../lib/convert";
import { LengthUnits } from "convert-units";
import { useClockOnTick } from "../clock";
import { getTraverseTimeRange } from "../../lib/cesium/TraverseUtility";

type DistanceEntry = {
  elapsed: number;
  distance: number;
};

function nearestDistanceIndexBinarySearch(
  distancesData: DistanceEntry[],
  secsFromStartUTC: number
) {
  let start = 0;
  let end = distancesData.length - 1;

  while (start <= end) {
    const middle = Math.floor((start + end) / 2);
    if (distancesData[middle].elapsed === secsFromStartUTC) {
      // found the key
      return middle;
    } else if (distancesData[middle].elapsed < secsFromStartUTC) {
      // continue searching to the right
      start = middle + 1;
    } else {
      // search searching to the left
      end = middle - 1;
    }
  }

  if (start === distancesData.length) {
    // set to the last item
    start = distancesData.length - 1;
  }

  // key wasn't found
  if (start === 0) {
    return -1;
  }

  if (
    Math.abs(secsFromStartUTC - distancesData[start - 1].elapsed) <
    Math.abs(secsFromStartUTC - distancesData[start].elapsed)
  ) {
    return start - 1;
  }
  return start;
}

export function TraverseInfo({ entity }: { entity: Entity }) {
  const [elapsedTime, setElapsedTime] = useState<number>();
  const [distance, setDistance] = useState<number>();
  const [distancesArray, setDistancesArray] = useState<DistanceEntry[]>();
  const [utcTime, setUtcTime] = useState<string>();
  const { map: viewer } = useCesiumContext();

  const traverseTimeRange = useMemo(
    () => getTraverseTimeRange(entity),
    [entity]
  );

  useClockOnTick(() => {
    if (!(entity.position instanceof SampledPositionProperty)) {
      setDistance(undefined);
      setElapsedTime(undefined);
      setUtcTime(undefined);
    }
    if (!isDefined(traverseTimeRange?.[0])) return;
    const traverseStartTime = traverseTimeRange[0];
    const tdiff = JulianDate.secondsDifference(
      viewer.clock.currentTime,
      traverseStartTime
    );
    setElapsedTime(tdiff);
    setUtcTime(JulianDate.toDate(viewer.clock.currentTime).toISOString());
    if (!isDefined(distancesArray)) return;
    const idx = nearestDistanceIndexBinarySearch(distancesArray, tdiff);
    let distance = -1;
    if (idx >= 0 && distancesArray[idx].elapsed !== tdiff) {
      distance = distancesArray[idx].distance;
      // refine measurement for last polyline segment
      const geodesic = new EllipsoidGeodesic();
      // find the time of the start of the segment
      const prevUTCTime = new JulianDate();
      const prevTimeIdx = distancesArray[idx].elapsed > tdiff ? idx - 1 : idx;
      JulianDate.addSeconds(
        traverseStartTime,
        distancesArray[prevTimeIdx].elapsed,
        prevUTCTime
      );
      let prevPoint = entity.position?.getValue(prevUTCTime);
      let endPoint = entity.position?.getValue(viewer.clock.currentTime);
      if (isDefined(prevPoint) && isDefined(endPoint)) {
        // convert cordinates to regular terrain reference system
        prevPoint = viewer.adjustCartesianInv(prevPoint);
        endPoint = viewer.adjustCartesianInv(endPoint);
        geodesic.setEndPoints(
          Cartographic.fromCartesian(prevPoint, viewer.scene.globe.ellipsoid),
          Cartographic.fromCartesian(endPoint, viewer.scene.globe.ellipsoid)
        );
        const preDistance = distancesArray[prevTimeIdx].distance;
        distance = preDistance + geodesic.surfaceDistance;
      }
    }
    setDistance(distance);
  }, 100);

  useEffect(() => {
    if (
      !(entity.position instanceof SampledPositionProperty) ||
      !isDefined(traverseTimeRange?.[0]) ||
      !isDefined(traverseTimeRange?.[1])
    ) {
      setDistancesArray(undefined);
      return;
    }

    // update the distances array
    const traverseStartTime = traverseTimeRange[0];
    const traverseStopTime = traverseTimeRange[1];
    const traverseDuration = JulianDate.secondsDifference(
      traverseStopTime,
      traverseStartTime
    );
    // divide into fixed array
    const deltaT = traverseDuration / 100;
    // minimum distance between two consecutive points
    const minDist = 1000;
    const distancesData: DistanceEntry[] = [];
    // add first point
    distancesData.push({
      elapsed: 0,
      distance: 0,
    });
    let prevPointTime = JulianDate.clone(traverseStartTime);
    let prevPointCoords = entity.position.getValue(traverseStartTime);
    let currentTime = new JulianDate();
    JulianDate.addSeconds(traverseStartTime, deltaT, currentTime);
    while (JulianDate.secondsDifference(traverseStopTime, currentTime) > 0) {
      let pointCoords = entity.position.getValue(currentTime);
      // check distance between two points, if it is too big then add intermediate point
      while (
        isDefined(pointCoords) &&
        isDefined(prevPointCoords) &&
        Cartesian3.distance(prevPointCoords, pointCoords) > minDist
      ) {
        currentTime = JulianDate.addSeconds(
          prevPointTime,
          JulianDate.secondsDifference(currentTime, prevPointTime) / 2,
          currentTime
        );
        pointCoords = entity.position.getValue(currentTime);
      }

      if (isDefined(pointCoords) && isDefined(prevPointCoords)) {
        const dist = Cartesian3.distance(prevPointCoords, pointCoords);
        distancesData.push({
          elapsed: JulianDate.secondsDifference(currentTime, traverseStartTime),
          distance: distancesData.length
            ? distancesData[distancesData.length - 1].distance + dist
            : dist,
        });
        prevPointCoords = Cartesian3.clone(pointCoords);
        prevPointTime = JulianDate.clone(currentTime);
        JulianDate.addSeconds(currentTime, deltaT, currentTime);
      }
    }
    setDistancesArray(distancesData);
  }, [entity.position, traverseTimeRange]);

  if (!isDefined(traverseTimeRange?.[0])) return;

  const distanceUnits = distance! > 1000 ? "km" : ("m" as LengthUnits);
  const distancePrecision = distanceUnits === "m" ? 1 : 3;
  return (
    <div className="border-t py-1 border-qmdark-100 mt-4 flex flex-col">
      <div className="flex flex-row">
        <h3 className="label">Traverse Info</h3>
      </div>
      <LabeledValue label={`UTC`} value={utcTime} />
      <LabeledValue
        label={`Elapsed Time`}
        value={isDefined(elapsedTime) ? niceFormatInterval(elapsedTime) : ""}
      />
      <LabeledValue
        label={`Distance Covered`}
        value={formatUnitValue(distance, {
          from: "m",
          to: distanceUnits,
          precision: distancePrecision,
          showUnits: true,
        })}
      />
    </div>
  );
}
